/**
 * EPL Admin Hooks - Hooks para panel de administración
 * 
 * Hooks para:
 * - Gestión de usuarios y asignación de sistemas EPL
 * - Analytics de IA y métricas de OpenAI
 * - Estadísticas del sistema
 */

import { useState, useEffect, useCallback } from 'react';
import { eplApi } from '../services/epl_api';

// =================== useEplAdminUsers ===================

/**
 * Hook para gestionar usuarios en admin
 */
export const useEplAdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    search: ''
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await eplApi.getAdminUsers(filters);
      
      setUsers(response.data || []);
    } catch (err) {
      setError(err.message);
      console.error('❌ Error fetching admin users:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const updateUserSystems = useCallback(async (userId, systems) => {
    try {
      setLoading(true);
      
      const response = await eplApi.updateUserSystems(userId, systems);
      
      // Actualizar lista local optimistamente
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, eplSystemsAssigned: systems }
          : user
      ));
      
      return response;
    } catch (err) {
      setError(err.message);
      // Refrescar en caso de error para mantener consistencia
      await fetchUsers();
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  const getUserStats = useCallback(async (userId) => {
    try {
      const response = await eplApi.getUserStats(userId);
      return response.data;
    } catch (err) {
      console.error('❌ Error fetching user stats:', err);
      throw err;
    }
  }, []);

  const deleteUser = useCallback(async (userId) => {
    try {
      setLoading(true);
      
      const response = await eplApi.deleteUser(userId);
      
      // Eliminar de la lista local optimistamente
      setUsers(prev => prev.filter(user => user.id !== userId));
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback(async (userId, userData) => {
    try {
      setLoading(true);
      
      const response = await eplApi.updateUser(userId, userData);
      
      // Actualizar en la lista local optimistamente
      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, ...userData }
          : user
      ));
      
      return response;
    } catch (err) {
      setError(err.message);
      // Refrescar en caso de error
      await fetchUsers();
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  const createUser = useCallback(async (userData) => {
    try {
      setLoading(true);
      
      const response = await eplApi.createUser(userData);
      
      // Refrescar lista para obtener el usuario nuevo con todas sus propiedades
      await fetchUsers();
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  const resetPassword = useCallback(async (userId, newPassword) => {
    try {
      setLoading(true);
      
      const response = await eplApi.resetUserPassword(userId, newPassword);
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    filters,
    setFilters,
    actions: {
      refresh: fetchUsers,
      updateSystems: updateUserSystems,
      getStats: getUserStats,
      deleteUser: deleteUser,
      updateUser: updateUser,
      createUser: createUser,
      resetPassword: resetPassword
    }
  };
};

// =================== useEplAIAnalytics ===================

/**
 * Hook para analytics de IA
 */
export const useEplAIAnalytics = (dateRange = {}) => {
  const [analytics, setAnalytics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await eplApi.getAIAnalytics(dateRange);
      
      setAnalytics(response.data);
    } catch (err) {
      setError(err.message);
      console.error('❌ Error fetching AI analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const response = await eplApi.getAILogs({ 
        limit: pagination.limit, 
        page 
      });
      
      setLogs(response.data || []);
      setPagination(prev => ({ 
        ...prev, 
        ...response.pagination,
        page 
      }));
    } catch (err) {
      setError(err.message);
      console.error('❌ Error fetching AI logs:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchAnalytics();
    fetchLogs(1);
  }, [fetchAnalytics]);

  return {
    analytics,
    logs,
    loading,
    error,
    pagination,
    actions: {
      refresh: fetchAnalytics,
      fetchLogs,
      nextPage: () => fetchLogs(pagination.page + 1),
      prevPage: () => fetchLogs(Math.max(1, pagination.page - 1))
    }
  };
};

// =================== useEplAdminDashboard ===================

/**
 * Hook para dashboard admin (combina métricas generales)
 */
export const useEplAdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalLabs: 0,
    totalEvaluations: 0,
    avgScore: 0,
    aiEvaluations: 0,
    tokensUsed: 0,
    avgResponseTime: 0,
    activeToday: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Combinar datos de diferentes endpoints
      const [usersRes, aiRes] = await Promise.all([
        eplApi.getAdminUsers({ role: 'all', status: 'all' }),
        eplApi.getAIAnalytics({})
      ]);
      
      const users = usersRes.data || [];
      const aiAnalytics = aiRes.data || {};
      
      setStats({
        totalUsers: users.length,
        totalLabs: users.reduce((sum, u) => sum + (u.completedLabs || 0), 0),
        totalEvaluations: aiAnalytics.summary?.totalEvaluations || 0,
        avgScore: Math.round(
          users.reduce((sum, u) => sum + (u.avgScore || 0), 0) / Math.max(users.length, 1)
        ),
        aiEvaluations: aiAnalytics.summary?.totalEvaluations || 0,
        tokensUsed: aiAnalytics.summary?.totalTokens || 0,
        avgResponseTime: aiAnalytics.summary?.avgResponseTime || 0,
        activeToday: users.filter(u => {
          if (!u.lastActive) return false;
          const lastActive = new Date(u.lastActive);
          const today = new Date();
          return lastActive.toDateString() === today.toDateString();
        }).length
      });
      
      // Actividad reciente (últimos 10 usuarios activos)
      const recentUsers = users
        .filter(u => u.lastActive)
        .sort((a, b) => new Date(b.lastActive) - new Date(a.lastActive))
        .slice(0, 10);
      
      setRecentActivity(recentUsers.map(u => ({
        user: u.nombre,
        action: `Completó ${u.completedLabs} laboratorio(s)`,
        time: u.lastActive,
        score: u.avgScore
      })));
      
    } catch (err) {
      setError(err.message);
      console.error('❌ Error fetching dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  return {
    stats,
    recentActivity,
    loading,
    error,
    actions: {
      refresh: fetchDashboardStats
    }
  };
};
